import { assign, createMachine } from "xstate";
import {
  fetchCountries,
  IRoamingInternationalCountry,
  IZone,
} from "@nju/data-access-portal-dashboard";
import {
  fetchOffers,
  IOffer,
  IOfferZone,
} from "@nju/data-access-portal-offers";
import { fetchOrderProducts } from "@nju/data-access-portal-product";
import { info, IPaymentMethod } from "@nju/data-access-portal-user";
import { pushSimpleEvent } from "@nju/data-layer";
import {
  IButtonType,
  IFilteredOffer,
  IOfferType,
  IPackageGb,
  IPackageType,
} from "./types";
import {
  filterPromoRoamingPackages,
  filterRoamingPackages,
  resolveOfferType,
  setDefaultOffer,
  setZeroPackage,
  ZERO_OFFER_ID,
} from "./utils";

type IEvent =
  | {
      type: "FETCH_DATA_SUCCESS";
      selectedCountry: IRoamingInternationalCountry | undefined;
      countries: IRoamingInternationalCountry[];
      offers: IOffer[];
      packageGb: IPackageGb;
      userFunds: number;
      primaryPaymentMethod: IPaymentMethod["methodType"] | undefined;
    }
  | { type: "SELECT_COUNTRY"; selectedCountry: IRoamingInternationalCountry }
  | {
      type: "SELECT_VOICE_PACKAGE";
      buttonType: IButtonType;
      packageType: IPackageType;
    }
  | {
      type: "SELECT_DATA_PACKAGE";
      buttonType: IButtonType;
      packageType: IPackageType;
    }
  | { type: "SUBMIT" }
  | { type: "SET_COUNTRY_SEARCH"; search: string }
  | { type: "SET_DEFAULT_PACKAGES"; packageType: IPackageType }
  | { type: "CLEAR_SEARCH_COUNTRY" }
  | { type: "ERROR" };

interface IContext {
  zeroOfferError: string | undefined;
  countries: IRoamingInternationalCountry[];
  filteredCountries: IRoamingInternationalCountry[];
  selectedCountry: IRoamingInternationalCountry | undefined;
  formError: string | undefined;
  offers: IOffer[];
  packageGb: IPackageGb;
  promoRoamingOffers: IFilteredOffer;
  roamingOffers: IFilteredOffer;
  selectedVoicePackage: IOffer | undefined;
  selectedDataPackage: IOffer | undefined;
  price: number | undefined;
  userFunds: number;
  countrySearch: string;
  offerType: IOfferType | undefined;
  isNotEnoughFunds: boolean;
  primaryPaymentMethod: IPaymentMethod["methodType"] | undefined;
  zone: IOfferZone | undefined;
}

export const selectOfferMachine = createMachine(
  {
    context: {
      zeroOfferError: undefined,
      countries: [],
      filteredCountries: [],
      selectedCountry: undefined,
      formError: undefined,
      offers: [],
      packageGb: {
        anniversaryDate: undefined,
        productOfferGrantValue: undefined,
        productOfferPrice: undefined,
      },
      promoRoamingOffers: {
        dataPackage: [],
        voicePackage: [],
      },
      roamingOffers: {
        dataPackage: [],
        voicePackage: [],
      },
      selectedVoicePackage: undefined,
      selectedDataPackage: undefined,
      price: undefined,
      userFunds: 0,
      countrySearch: "",
      offerType: undefined,
      isNotEnoughFunds: false,
      primaryPaymentMethod: undefined,
      zone: undefined,
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "loading",
    states: {
      loading: {
        invoke: {
          src: "fetchData",
        },
        on: {
          FETCH_DATA_SUCCESS: [
            // check whether user have pass query parameter eq `?country=Liechtenstein`, if he has,
            // then set this country and redirect to countrySelected state
            {
              cond: "isCountryFromUrl",
              actions: ["setData", "resolveOfferType"],
              target: "countrySelected",
            },
            {
              actions: "setData",
              target: "ready",
            },
          ],
          ERROR: {
            target: "error",
          },
        },
      },
      ready: {
        id: "ready",
        on: {
          SET_COUNTRY_SEARCH: [
            {
              cond: "shouldFilterCountries",
              actions: ["setCountrySearch", "filterCountries"],
            },
            {
              actions: "setCountrySearch",
            },
          ],
          SELECT_COUNTRY: {
            actions: ["selectCountry", "resolveOfferType"],
            target: "countrySelected",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasInputErrors",
            target: "ready",
          },
          {
            target: "success",
          },
        ],
      },
      countrySelected: {
        initial: "resolveCountryType",
        states: {
          resolveCountryType: {
            always: [
              {
                cond: "isOfferEu",
                actions: [
                  "calculateEuPackagePrice",
                  "checkIsNotEnoughFunds",
                  "resetZeroOfferError",
                  "setOfferZone",
                ],
                target: "ready",
              },
              {
                actions: [
                  "filterPackages",
                  "setInitialVoicePackage",
                  "setInitialDataPackage",
                  "calculatePackagePrice",
                  "checkIsNotEnoughFunds",
                  "setZeroPackages",
                  "resetZeroOfferError",
                  "setOfferZone",
                ],
                target: "ready",
              },
            ],
          },
          ready: {
            on: {
              SUBMIT: [
                {
                  cond: "isZeroOffer",
                  actions: "setZeroOfferError",
                },
                {
                  actions: "onSubmitSuccess",
                },
              ],
              SET_COUNTRY_SEARCH: [
                {
                  cond: "shouldFilterCountries",
                  actions: ["setCountrySearch", "filterCountries"],
                },
                {
                  actions: "setCountrySearch",
                },
              ],
              SET_DEFAULT_PACKAGES: {
                actions: [
                  "setDefaultDataPackage",
                  "setDefaultVoicePackage",
                  "calculatePackagePrice",
                  "checkIsNotEnoughFunds",
                  "resetZeroOfferError",
                  "setOfferZone",
                ],
              },
              SELECT_COUNTRY: {
                actions: ["selectCountry", "resolveOfferType"],
                target: "resolveCountryType",
              },
              CLEAR_SEARCH_COUNTRY: {
                actions: "clearSearchCountry",
                target: "#ready",
              },
              SELECT_DATA_PACKAGE: {
                actions: [
                  "selectDataPackage",
                  "calculatePackagePrice",
                  "checkIsNotEnoughFunds",
                  "resetZeroOfferError",
                ],
              },
              SELECT_VOICE_PACKAGE: {
                actions: [
                  "selectVoicePackage",
                  "calculatePackagePrice",
                  "checkIsNotEnoughFunds",
                  "resetZeroOfferError",
                ],
              },
            },
          },
        },
      },
      success: {
        type: "final",
      },
      error: {
        type: "final",
      },
    },
  },
  {
    actions: {
      setZeroOfferError: assign<IContext, IEvent>(() => {
        pushSimpleEvent({
          event: "activity_service",
          eventName: "ChoosePack",
        });

        return {
          zeroOfferError:
            "Aby przejść dalej wybierz, ile minut/SMS-ów/MMS-ów lub GB potrzebujesz.",
        };
      }),
      resetZeroOfferError: assign<IContext, IEvent>(() => {
        return {
          zeroOfferError: undefined,
        };
      }),
      setOfferZone: assign<IContext, IEvent>((context) => {
        const selectedVoicePackageZone = context.selectedVoicePackage?.zone;
        const selectedDataPackageZone = context.selectedDataPackage?.zone;

        if (selectedVoicePackageZone && selectedDataPackageZone) {
          return { zone: selectedVoicePackageZone };
        }

        if (selectedVoicePackageZone) {
          return { zone: selectedVoicePackageZone };
        }

        if (selectedDataPackageZone) {
          return { zone: selectedDataPackageZone };
        }

        if (
          !selectedVoicePackageZone &&
          !selectedDataPackageZone &&
          context.offerType === "EU"
        ) {
          return { zone: "EU" };
        }

        return { zone: undefined };
      }),
      setCountrySearch: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_COUNTRY_SEARCH") {
          return {
            countrySearch: event.search,
          };
        }

        return context;
      }),
      setDefaultVoicePackage: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_DEFAULT_PACKAGES") {
          if (event.packageType === "PROMO") {
            return {
              selectedVoicePackage: setDefaultOffer({
                offers: context.promoRoamingOffers.voicePackage,
              }),
            };
          }
          if (event.packageType === "STANDARD") {
            return {
              selectedVoicePackage: setDefaultOffer({
                offers: context.roamingOffers.voicePackage,
              }),
            };
          }
        }

        return context;
      }),
      setDefaultDataPackage: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_DEFAULT_PACKAGES") {
          if (event.packageType === "PROMO") {
            return {
              selectedDataPackage: setDefaultOffer({
                offers: context.promoRoamingOffers.dataPackage,
              }),
            };
          }
          if (event.packageType === "STANDARD") {
            return {
              selectedDataPackage: setDefaultOffer({
                offers: context.roamingOffers.dataPackage,
              }),
            };
          }
        }

        return context;
      }),
      setInitialVoicePackage: assign<IContext, IEvent>((context) => {
        const isPromoOffer =
          context?.offerType === "BREXIT" || context?.offerType === "HT";

        if (
          context.promoRoamingOffers.voicePackage.length > 0 &&
          isPromoOffer
        ) {
          return {
            selectedVoicePackage: setDefaultOffer({
              offers: context.promoRoamingOffers.voicePackage,
            }),
          };
        }

        if (context.roamingOffers.voicePackage.length > 0 && !isPromoOffer) {
          return {
            selectedVoicePackage: setDefaultOffer({
              offers: context.roamingOffers.voicePackage,
            }),
          };
        }

        return context;
      }),
      setInitialDataPackage: assign<IContext, IEvent>((context) => {
        const isPromoOffer =
          context?.offerType === "BREXIT" || context?.offerType === "HT";

        if (context.promoRoamingOffers.dataPackage.length > 0 && isPromoOffer) {
          return {
            selectedDataPackage: setDefaultOffer({
              offers: context.promoRoamingOffers.dataPackage,
            }),
          };
        }

        if (context.roamingOffers.dataPackage.length > 0 && !isPromoOffer) {
          return {
            selectedDataPackage: setDefaultOffer({
              offers: context.roamingOffers.dataPackage,
            }),
          };
        }

        return context;
      }),
      resolveOfferType: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SELECT_COUNTRY") {
          return {
            offerType: resolveOfferType({
              roamingZone: event.selectedCountry.zones.find((zone) => {
                return !zone.isPromo;
              })?.name as IZone["name"],
              promoRoamingZone: event.selectedCountry.zones
                ?.sort(function (a, b) {
                  return a.priority - b.priority;
                })
                ?.find((zone) => {
                  return zone.isPromo;
                })?.name as IZone["name"],
            }),
          };
        }

        if (context.selectedCountry) {
          return {
            offerType: resolveOfferType({
              roamingZone: context.selectedCountry.zones.find((zone) => {
                return !zone.isPromo;
              })?.name as IZone["name"],
              promoRoamingZone: context.selectedCountry.zones
                ?.sort(function (a, b) {
                  return a.priority - b.priority;
                })
                ?.find((zone) => {
                  return zone.isPromo;
                })?.name as IZone["name"],
            }),
          };
        }

        return context;
      }),
      checkIsNotEnoughFunds: assign<IContext, IEvent>((context) => {
        if (context.offerType === "EU") {
          return {
            isNotEnoughFunds: !!(
              context.packageGb.productOfferPrice &&
              context.primaryPaymentMethod === "DIGITAL_WALLET" &&
              context.userFunds < context.packageGb.productOfferPrice
            ),
          };
        }

        return {
          isNotEnoughFunds: !!(
            context.price &&
            context.primaryPaymentMethod === "DIGITAL_WALLET" &&
            context.userFunds < context.price
          ),
        };
      }),
      calculateEuPackagePrice: assign<IContext, IEvent>((context) => {
        return {
          price: context.packageGb.productOfferPrice,
        };
      }),
      calculatePackagePrice: assign<IContext, IEvent>((context) => {
        const selectedVoicePackagePrice =
          context.selectedVoicePackage?.productOfferingPrice?.price?.amount;
        const selectedDataPackagePrice =
          context.selectedDataPackage?.productOfferingPrice?.price?.amount;

        if (selectedVoicePackagePrice && !selectedDataPackagePrice) {
          return {
            price: selectedVoicePackagePrice,
          };
        }

        if (selectedDataPackagePrice && !selectedVoicePackagePrice) {
          return {
            price: selectedDataPackagePrice,
          };
        }

        if (selectedVoicePackagePrice && selectedDataPackagePrice) {
          return {
            price: selectedVoicePackagePrice + selectedDataPackagePrice,
          };
        }

        return {
          price: 0,
        };
      }),
      validateForm: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SUBMIT") {
          pushSimpleEvent({
            event: "activity_service",
            eventName: "EnterCountry",
          });

          return {
            formError: context.selectedCountry ? undefined : "Wpisz kraj.",
          };
        }

        return context;
      }),
      selectDataPackage: assign<IContext, IEvent>((context, event) => {
        if (
          event.type === "SELECT_DATA_PACKAGE" &&
          context.selectedDataPackage
        ) {
          if (event.packageType === "PROMO") {
            const dataPackage = context.promoRoamingOffers.dataPackage;
            const currentOfferIndex = dataPackage.indexOf(
              context.selectedDataPackage,
            );

            if (event.buttonType === "PREVIOUS") {
              return {
                selectedDataPackage: dataPackage[currentOfferIndex - 1],
              };
            }

            if (event.buttonType === "NEXT") {
              return {
                selectedDataPackage: dataPackage[currentOfferIndex + 1],
              };
            }
          }

          if (event.packageType === "STANDARD") {
            const dataPackage = context.roamingOffers.dataPackage;
            const currentOfferIndex = dataPackage.indexOf(
              context.selectedDataPackage,
            );

            if (event.buttonType === "PREVIOUS") {
              return {
                selectedDataPackage: dataPackage[currentOfferIndex - 1],
              };
            }

            if (event.buttonType === "NEXT") {
              return {
                selectedDataPackage: dataPackage[currentOfferIndex + 1],
              };
            }
          }
        }

        return context;
      }),
      selectVoicePackage: assign<IContext, IEvent>((context, event) => {
        if (
          event.type === "SELECT_VOICE_PACKAGE" &&
          context.selectedVoicePackage
        ) {
          const voicePackage = context.promoRoamingOffers.voicePackage;

          if (event.packageType === "PROMO") {
            const currentOfferIndex = voicePackage.indexOf(
              context.selectedVoicePackage,
            );

            if (event.buttonType === "PREVIOUS") {
              return {
                selectedVoicePackage: voicePackage[currentOfferIndex - 1],
              };
            }

            if (event.buttonType === "NEXT") {
              return {
                selectedVoicePackage: voicePackage[currentOfferIndex + 1],
              };
            }
          }

          if (event.packageType === "STANDARD") {
            const voicePackage = context.roamingOffers.voicePackage;
            const currentOfferIndex = voicePackage.indexOf(
              context.selectedVoicePackage,
            );

            if (event.buttonType === "PREVIOUS") {
              return {
                selectedVoicePackage: voicePackage[currentOfferIndex - 1],
              };
            }

            if (event.buttonType === "NEXT") {
              return {
                selectedVoicePackage: voicePackage[currentOfferIndex + 1],
              };
            }
          }
        }

        return context;
      }),
      filterCountries: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_COUNTRY_SEARCH") {
          return {
            filteredCountries: context.countries.filter(function (country) {
              if (country.searchTags) {
                return country.searchTags
                  .toLowerCase()
                  .includes(event.search.toLowerCase());
              }

              return false;
            }),
          };
        }

        return context;
      }),
      filterPackages: assign<IContext, IEvent>((context) => {
        return {
          roamingOffers: {
            dataPackage: filterRoamingPackages({
              offers: context.offers,
              id: "DATA_PACKAGE",
              roamingZone: context.selectedCountry?.zones.find((zone) => {
                return !zone.isPromo;
              })?.name as IZone["name"],
            }),
            voicePackage: filterRoamingPackages({
              offers: context.offers,
              id: "VOICE_PACKAGE",
              roamingZone: context.selectedCountry?.zones.find((zone) => {
                return !zone.isPromo;
              })?.name as IZone["name"],
            }),
          },
          promoRoamingOffers: {
            dataPackage: filterPromoRoamingPackages({
              offers: context.offers,
              id: "DATA_PACKAGE",
              promoRoamingZones:
                context.selectedCountry?.zones
                  ?.filter((zone) => {return zone.isPromo})
                  ?.sort((a, b) => {return a.priority - b.priority})
                  ?.map((zone) => {return zone.name}) ?? [],
            }),
            voicePackage: filterPromoRoamingPackages({
              offers: context.offers,
              id: "VOICE_PACKAGE",
              promoRoamingZones:
                context.selectedCountry?.zones
                  ?.filter((zone) => {return zone.isPromo})
                  ?.sort((a, b) => {return a.priority - b.priority})
                  ?.map((zone) => {return zone.name}) ?? [],
            }),
          },
        };
      }),
      clearSearchCountry: assign<IContext, IEvent>((context, event) => {
        if (event.type === "CLEAR_SEARCH_COUNTRY") {
          return {
            selectedCountry: undefined,
            filteredCountries: undefined,
            offersForZone: undefined,
            formError: undefined,
            zeroOfferError: undefined,
            isNotEnoughFunds: false,
          };
        }

        return context;
      }),
      setData: assign<IContext, IEvent>((context, event) => {
        if (event.type === "FETCH_DATA_SUCCESS") {
          return {
            selectedCountry: event.selectedCountry,
            countries: event.countries,
            filteredCountries: event.countries,
            offers: event.offers,
            packageGb: event.packageGb,
            userFunds: event.userFunds,
            primaryPaymentMethod: event.primaryPaymentMethod,
          };
        }

        return context;
      }),
      selectCountry: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SELECT_COUNTRY") {
          return {
            selectedCountry: event.selectedCountry,
            selectedVoicePackage: undefined,
            selectedDataPackage: undefined,
            formError: undefined,
          };
        }

        return context;
      }),
      setZeroPackages: assign<IContext, IEvent>((context) => {
        return {
          roamingOffers: {
            dataPackage:
              context.roamingOffers.dataPackage.length > 0
                ? [
                    setZeroPackage({ id: "DATA_PACKAGE" }),
                    ...context.roamingOffers.dataPackage,
                  ]
                : context.roamingOffers.dataPackage,
            voicePackage:
              context.roamingOffers.voicePackage.length > 0
                ? [
                    setZeroPackage({ id: "VOICE_PACKAGE" }),
                    ...context.roamingOffers.voicePackage,
                  ]
                : context.roamingOffers.voicePackage,
          },
          promoRoamingOffers: {
            dataPackage:
              context.promoRoamingOffers.dataPackage.length > 0
                ? [
                    setZeroPackage({ id: "DATA_PACKAGE" }),
                    ...context.promoRoamingOffers.dataPackage,
                  ]
                : context.promoRoamingOffers.dataPackage,
            voicePackage:
              context.promoRoamingOffers.voicePackage.length > 0
                ? [
                    setZeroPackage({ id: "VOICE_PACKAGE" }),
                    ...context.promoRoamingOffers.voicePackage,
                  ]
                : context.promoRoamingOffers.voicePackage,
          },
        };
      }),
    },
    services: {
      fetchData: ({ countrySearch }) => {
        return async (callback) => {
          try {
            const countries = (
              await fetchCountries({
                brand: "NJU",
                category: "ROAMING",
              })
            ).unwrap();
            const user = (await info()).unwrap();
            const offers = (await fetchOffers(true)).unwrap();
            const products = (await fetchOrderProducts()).unwrap();

            const packageGb = {
              anniversaryDate:
                products.mobileProducts.mobileAccess?.anniversaryDate,
              productOfferGrantValue: offers.dataPackage?.balances?.find(
                (item) => {
                  return ["21268", "21129"].includes(item.balanceId);
                },
              )?.grantValue,
              productOfferPrice:
                offers.dataPackage?.productOfferingPrice?.price.amount,
            };

            if (countrySearch) {
              const selectedCountry = countries.find((country) => {
                return country.displayName.pl === countrySearch;
              });

              return callback({
                type: "FETCH_DATA_SUCCESS",
                countries,
                packageGb,
                selectedCountry,
                offers: offers.roamingPackages,
                userFunds: user.currentFunds,
                primaryPaymentMethod: user.primaryPaymentMethod?.methodType,
              });
            }

            return callback({
              type: "FETCH_DATA_SUCCESS",
              countries,
              packageGb,
              selectedCountry: undefined,
              offers: offers.roamingPackages,
              userFunds: user.currentFunds,
              primaryPaymentMethod: user.primaryPaymentMethod?.methodType,
            });
          } catch {
            callback({ type: "ERROR" });
          }
        };
      },
    },
    guards: {
      hasInputErrors: ({ formError }) => {
        return formError !== undefined;
      },
      isOfferEu: ({ offerType }) => {
        return offerType === "EU";
      },
      isCountryFromUrl: ({ countrySearch }, event) => {
        if (event.type === "FETCH_DATA_SUCCESS") {
          return !!(countrySearch && event.selectedCountry);
        }

        return false;
      },
      shouldFilterCountries: ({ countrySearch }) => {
        return countrySearch.length >= 2;
      },
      isZeroOffer: ({ selectedDataPackage, selectedVoicePackage }) => {
        if (selectedDataPackage && selectedVoicePackage) {
          return (
            selectedDataPackage.id === ZERO_OFFER_ID &&
            selectedVoicePackage.id === ZERO_OFFER_ID
          );
        }

        if (selectedDataPackage && !selectedVoicePackage) {
          return selectedDataPackage.id === ZERO_OFFER_ID;
        }

        if (!selectedDataPackage && selectedVoicePackage) {
          return selectedVoicePackage.id === ZERO_OFFER_ID;
        }

        return false;
      },
    },
  },
);
